import { fmtDate, parseLocalDate } from './formatters';

/**
 * VIGÊNCIA DO ITEM DA ASSINATURA — o que a data faz na fatura, dito na tela.
 *
 * O backend recorta o período faturado pela vigência do item
 * (`invoices/helpers/vigencia-item.util.ts`, PR #224 do back): item que nasce
 * ou morre no meio do período é cobrado pelos dias em que existe, com os DIAS
 * CORRIDOS do período como divisor. Esta função refaz a mesma conta para a tela
 * mostrar "28 de 30 dias, R$ 932,40" ao lado do campo, que é exatamente a conta
 * que antes alguém fazia na mão (e errava).
 *
 * ⚠️ O período NÃO é sempre o mês civil da data, e a competência da fatura
 * NÃO é sempre o mês do serviço. Mesma regra da geração no backend:
 *   - cliente com dia de ciclo > 1: período do dia do ciclo até a véspera dele
 *     no mês seguinte; a fatura é a do mês em que o período termina;
 *   - órgão público: mês civil, faturado na competência SEGUINTE (postecipado);
 *   - demais: mês civil, faturado na própria competência.
 *
 * Data ilegível ou intervalo invertido: o backend cobra o mês cheio e grita no
 * log. Aqui vira aviso, para quem digitou corrigir antes de salvar.
 */

export interface CicloDeFaturamento {
  /** `clients.billingCycleStartDay` — 1 ou vazio = mês civil. */
  cycleDay?: number | null;
  /** `clients.isPublicSector` — fatura o mês anterior. */
  publico?: boolean;
}

export interface EfeitoVigencia {
  aviso: boolean;
  linhas: string[];
}

const DATA = /^\d{4}-\d{2}-\d{2}$/;
const DIA_MS = 86400000;

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const competencia = (d: Date) => `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
/** Dias corridos entre duas datas, contando as duas pontas. */
const diasInclusivos = (de: string, ate: string) =>
  Math.round((parseLocalDate(ate).getTime() - parseLocalDate(de).getTime()) / DIA_MS) + 1;
const brl = (v: number) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** O período de faturamento que contém a data, e a competência que o fatura. */
export function periodoQueContem(data: string, ciclo: CicloDeFaturamento): { inicio: string; fim: string; competencia: string } {
  const d = parseLocalDate(data);
  const y = d.getFullYear();
  const m = d.getMonth();
  const cycleDay = Number(ciclo.cycleDay) || 1;
  if (cycleDay > 1) {
    const mesInicio = d.getDate() >= cycleDay ? m : m - 1;
    const inicio = new Date(y, mesInicio, cycleDay);
    const fim = new Date(y, mesInicio + 1, cycleDay - 1);
    return { inicio: iso(inicio), fim: iso(fim), competencia: competencia(fim) };
  }
  const inicio = new Date(y, m, 1);
  const fim = new Date(y, m + 1, 0);
  const fatura = ciclo.publico ? new Date(y, m + 1, 1) : inicio;
  return { inicio: iso(inicio), fim: iso(fim), competencia: competencia(fatura) };
}

function linhaDoRecorte(rotulo: string, de: string, ate: string, periodo: ReturnType<typeof periodoQueContem>, valorCheio: number): string {
  const diasTotal = diasInclusivos(periodo.inicio, periodo.fim);
  const dias = diasInclusivos(de, ate);
  const onde = `período ${fmtDate(periodo.inicio)} a ${fmtDate(periodo.fim)} (fatura ${periodo.competencia})`;
  if (dias >= diasTotal) return `${rotulo}: ${onde} cobra o mês cheio.`;
  const valor = valorCheio ? `: ${brl(round2((valorCheio * dias) / diasTotal))} de ${brl(valorCheio)}` : '';
  return `${rotulo}: ${onde} cobra ${dias} de ${diasTotal} dias${valor}.`;
}

/** O que a vigência do item faz na cobrança. `null` = item sem vigência própria. */
export function efeitoDaVigencia(
  item: { vigenciaInicio?: string | null; vigenciaFim?: string | null; totalValue?: number | string | null },
  ciclo: CicloDeFaturamento,
): EfeitoVigencia | null {
  const ini = item.vigenciaInicio ? String(item.vigenciaInicio).slice(0, 10) : null;
  const fim = item.vigenciaFim ? String(item.vigenciaFim).slice(0, 10) : null;
  if (!ini && !fim) return null;
  if ((ini && !DATA.test(ini)) || (fim && !DATA.test(fim))) {
    return { aviso: true, linhas: ['Data ilegível: o faturamento ignora a vigência e cobra o mês cheio.'] };
  }
  if (ini && fim && fim < ini) {
    return { aviso: true, linhas: ['O fim está antes do início: o faturamento ignora a vigência e cobra o mês cheio.'] };
  }

  const valorCheio = Number(item.totalValue) || 0;
  const pIni = ini ? periodoQueContem(ini, ciclo) : null;
  const pFim = fim ? periodoQueContem(fim, ciclo) : null;

  // Entra e sai no mesmo período: uma conta só.
  if (ini && fim && pIni && pFim && pIni.inicio === pFim.inicio) {
    return { aviso: false, linhas: [linhaDoRecorte(`De ${fmtDate(ini)} a ${fmtDate(fim)}`, ini, fim, pIni, valorCheio)] };
  }
  const linhas: string[] = [];
  if (ini && pIni) linhas.push(linhaDoRecorte(`Entra em ${fmtDate(ini)}`, ini, pIni.fim, pIni, valorCheio));
  if (fim && pFim) linhas.push(linhaDoRecorte(`Último dia ${fmtDate(fim)}`, pFim.inicio, fim, pFim, valorCheio));
  return { aviso: false, linhas };
}

export type SituacaoVigencia = 'vigente' | 'futuro' | 'encerrado';

/**
 * O item vale na data? Mesma leitura do backend: data ilegível ou intervalo
 * invertido contam como vigente, porque é assim que o faturamento cobra.
 */
export function situacaoDaVigencia(
  item: { vigenciaInicio?: string | null; vigenciaFim?: string | null },
  hoje: string,
): SituacaoVigencia {
  const ini = item.vigenciaInicio ? String(item.vigenciaInicio).slice(0, 10) : null;
  const fim = item.vigenciaFim ? String(item.vigenciaFim).slice(0, 10) : null;
  if ((ini && !DATA.test(ini)) || (fim && !DATA.test(fim)) || (ini && fim && fim < ini)) return 'vigente';
  if (ini && ini > hoje) return 'futuro';
  if (fim && fim < hoje) return 'encerrado';
  return 'vigente';
}

/** Soma de `totalValue` só dos itens que valem na data. */
export function somaVigente(itens: any[], hoje: string): number {
  return round2(itens.filter((i) => situacaoDaVigencia(i, hoje) === 'vigente').reduce((s, i) => s + (Number(i.totalValue) || 0), 0));
}
